import {useMemo} from 'react'
import {Cpu} from 'lucide-react'
import type {Model} from '../types'
import {cn} from '../lib/utils'
import {Field} from './ui/Field'

export function ModelAllowlist({
  label,
  hint,
  models,
  selected,
  onChange,
  emptyHint,
}: {
  label: string
  hint?: string
  models: Model[]
  selected: string[]
  onChange: (ids: string[]) => void
  emptyHint?: string
}) {
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id])

  const allIds = models.map((model) => model.id)
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.includes(id))
  const groups = useMemo(() => {
    const byProvider = new Map<string, Model[]>()
    models.forEach((model) => {
      if (!byProvider.has(model.provider)) byProvider.set(model.provider, [])
      byProvider.get(model.provider)!.push(model)
    })
    return Array.from(byProvider.entries())
  }, [models])

  return (
    <Field label={label} hint={hint} className='mb-0 mt-4'>
      {models.length === 0 ? (
        <p className='rounded-lg bg-cloud px-3 py-2 text-xs text-slate'>
          {emptyHint ?? 'Nenhum modelo disponível.'}
        </p>
      ) : (
        <div className='rounded-xl border border-line p-3'>
          <div className='mb-2 flex items-center justify-between'>
            <span className='flex items-center gap-1.5 text-xs font-medium text-slate'>
              <Cpu className='h-3.5 w-3.5' />
              {selected.filter((id) => allIds.includes(id)).length}/{allIds.length} selecionado(s)
            </span>
            <button
              type='button'
              className='text-xs font-medium text-brand-orange hover:underline'
              onClick={() =>
                onChange(
                  allSelected
                    ? selected.filter((id) => !allIds.includes(id))
                    : Array.from(new Set([...selected, ...allIds]))
                )
              }
            >
              {allSelected ? 'Limpar todos' : 'Selecionar todos'}
            </button>
          </div>
          <div className='max-h-56 space-y-3 overflow-y-auto scrollbar-thin'>
            {groups.map(([provider, providerModels]) => (
              <div key={provider}>
                <p className='mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate/70'>
                  {provider}
                </p>
                <div className='grid grid-cols-1 gap-1.5 sm:grid-cols-2'>
                  {providerModels.map((model) => {
                    const active = selected.includes(model.id)
                    return (
                      <button
                        key={model.id}
                        type='button'
                        onClick={() => toggle(model.id)}
                        className={cn(
                          'flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left text-sm transition-colors',
                          active
                            ? 'border-brand-orange bg-brand-orange/5 text-ink'
                            : 'border-line bg-white text-slate hover:bg-cloud'
                        )}
                      >
                        <span
                          className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                            active
                              ? 'border-brand-orange bg-brand-orange text-white'
                              : 'border-line'
                          )}
                        >
                          {active && <span className='text-[10px] leading-none'>✓</span>}
                        </span>
                        <span className='truncate'>{model.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Field>
  )
}
